/**
 * Defensive wrappers around Web Storage. Private-mode Safari, full quotas and
 * corrupted values must never break the app, so every operation degrades to a
 * no-op / fallback instead of throwing.
 */

const memoryFallback = new Map<string, string>()

function backend(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    const probe = '__songster_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    return null
  }
}

export function readString(key: string): string | null {
  try {
    const store = backend()
    if (store) return store.getItem(key)
  } catch {
    // fall through to the in-memory fallback
  }
  return memoryFallback.get(key) ?? null
}

export function writeString(key: string, value: string): void {
  memoryFallback.set(key, value)
  try {
    backend()?.setItem(key, value)
  } catch {
    // Storage full or blocked — the in-memory copy keeps this session working.
  }
}

export function removeKey(key: string): void {
  memoryFallback.delete(key)
  try {
    backend()?.removeItem(key)
  } catch {
    // nothing to do
  }
}

/** Reads and parses JSON, returning null for missing or unusable values. */
export function readJson<T>(key: string, isValid: (value: unknown) => value is T): T | null {
  const raw = readString(key)
  if (raw === null) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    removeKey(key)
    return null
  }
  if (!isValid(parsed)) {
    removeKey(key)
    return null
  }
  return parsed
}

export function writeJson(key: string, value: unknown): void {
  try {
    writeString(key, JSON.stringify(value))
  } catch {
    // Unserializable value — treat as "not persisted" rather than crashing.
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
